import type { StaffCatalogSearchBody, StaffCatalogSearchResult } from "@lafayette/schemas";
import { useQuery } from "@tanstack/react-query";

import { useCatalogRuntime } from "../runtime";

// The reads of the search page. Search is a POST, but it changes nothing, so it is cached like a read:
// the key is the whole body. A staff answer can hold items that are not published, so nothing stays in
// the cache after the page lets go (`gcTime: 0`), and nothing is written to browser storage.

const searchKeys = {
  run: (body: StaffCatalogSearchBody) => ["catalog", "search", body] as const,
  dietary: ["catalog", "search", "dietary-tags"] as const,
};

/** The search. It runs when `body` is not `null`. A failed call is not retried: the page offers a retry. */
export function useCatalogSearch(body: StaffCatalogSearchBody | null) {
  const { api } = useCatalogRuntime();
  return useQuery<StaffCatalogSearchResult>({
    queryKey: searchKeys.run(body ?? {}),
    queryFn: ({ signal }) => api.searchCatalog(body ?? {}, { signal }),
    enabled: body !== null,
    gcTime: 0,
    retry: false,
    refetchOnWindowFocus: false,
  });
}

/** The DIETARY tags the item pages know, for the checkbox list. Without a list the form takes free codes. */
export function useDietaryTags(enabled: boolean) {
  const { api } = useCatalogRuntime();
  return useQuery({
    queryKey: searchKeys.dietary,
    queryFn: ({ signal }) => api.searchTags({ namespace: "DIETARY", limit: 50 }, { signal }),
    enabled,
    retry: false,
    refetchOnWindowFocus: false,
    staleTime: 5 * 60_000,
  });
}
